import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Carousel, CarouselVariant, I18N_NAMESPACES, SliderNavColor, SliderNavVariant, Tag, TagColor, } from '@as/cotopaxi';
import { mergeProductFeatures } from '@as-react/common-utils';
import { Box, TextAlpha } from '@yonderland/andes-react';
import { useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { MIN_SCREEN_WIDTH_DESKTOP, MIN_SCREEN_WIDTH_DESKTOP_LARGE, MIN_SCREEN_WIDTH_TABLET, } from '../../constants/ScreenWidthConstants';
import { SLIDES_AMOUNT_DESKTOP, SLIDES_AMOUNT_DESKTOP_LARGE } from '../../constants/SliderConstants';
import { useProductGroupAnalytics } from '../../hooks/useProductGroupAnalytics';
import { useProductTileHelpers } from '../../hooks/useProductTileHelpers';
import { makeGetProduct } from '../../redux/product/selectors/ProductSelector';
import { createProductDetailToProductListerAdapter } from '../../util/ProductUtil';
import { generateTestId } from '../../util/TestUtil';
import { ProductGroupPdpComparisonProduct } from './ProductGroupPdpComparisonProduct';
const SLIDES_AMOUNT_MOBILE = 1.5;
const SLIDES_AMOUNT_TABLET = 3.5;
const MAX_PRODUCTS_SIZE = 5;
const MAX_FEATURES_SIZE = 5;
export var GRID_AREAS;
(function (GRID_AREAS) {
    GRID_AREAS["PRODUCT"] = "product";
    GRID_AREAS["PRODUCT_TILE"] = "tile";
    GRID_AREAS["FEATURE"] = "feature";
    GRID_AREAS["FEATURE_TITLE"] = "title";
    GRID_AREAS["FEATURE_VALUE"] = "value";
    GRID_AREAS["COMPARE"] = "compare";
})(GRID_AREAS || (GRID_AREAS = {}));
const repeat = (count, prefix, children = null) => {
    if (!children) {
        return Array.from({ length: count }, (_, index) => (count > 1 ? `${prefix}-${index}` : `${prefix}`));
    }
    else {
        return Array.from({ length: count }, (_, index) => children.map(child => `${prefix}-${index}-${child}`)).flat(1);
    }
};
const generateGridTemplateAreas = (columnStructure, rowStructure) => {
    let templateAreas = '';
    const rows = rowStructure.flat(2);
    const columns = columnStructure.flat(2);
    rows.forEach(row => {
        let templateAreaRow = '"';
        columns.forEach(col => {
            templateAreaRow += `${col}-${row} `;
        });
        templateAreas += templateAreaRow.trim() + '"\n';
    });
    return templateAreas.trim();
};
const COMPARE_PRODUCT_INDEX_ADDITION = 1;
const GRID_TEMPLATE_AREAS = generateGridTemplateAreas([repeat(MAX_PRODUCTS_SIZE, GRID_AREAS.PRODUCT)], [
    [GRID_AREAS.PRODUCT_TILE],
    repeat(MAX_FEATURES_SIZE, GRID_AREAS.FEATURE, [GRID_AREAS.FEATURE_TITLE, GRID_AREAS.FEATURE_VALUE]),
    [GRID_AREAS.COMPARE],
]);
export const ProductGroupPdpComparison = ({ analyticsAreaName, productGroupId, products, title, productsFeaturesRecord, analyticsProductGroupName, currentProductHasFeatures, testId = 'product-group-pdp-comparison', }) => {
    const { t } = useTranslation(I18N_NAMESPACES.product);
    const { handleWishlistToggle, handleUpdateCompareList, compareItems } = useProductTileHelpers();
    const productUniqueKey = useId();
    const productDetail = useSelector(makeGetProduct());
    const productGroupRef = useRef(null);
    const { handleItemClickAnalytics } = useProductGroupAnalytics(Boolean(analyticsProductGroupName), productGroupRef, analyticsProductGroupName, title, products);
    if (!(products === null || products === void 0 ? void 0 : products.length)) {
        return null;
    }
    const currentProduct = currentProductHasFeatures && productDetail && productDetail.productColorVariations
        ? createProductDetailToProductListerAdapter(productDetail)
        : null;
    const productsWithFeatures = mergeProductFeatures(currentProduct ? [currentProduct, ...products] : products, productsFeaturesRecord, MAX_PRODUCTS_SIZE, currentProduct === null || currentProduct === void 0 ? void 0 : currentProduct.productId, MAX_FEATURES_SIZE);
    if (!(productsWithFeatures === null || productsWithFeatures === void 0 ? void 0 : productsWithFeatures.length)) {
        return null;
    }
    const createCurrentProductTag = () => (_jsx(Tag, { color: TagColor.SEPTENARY, children: t('product.group.current.tag') }, "current_product_tag"));
    const carouselConfig = {
        variant: CarouselVariant.PRODUCT_TILE_COMPARISON,
        arrowsConfig: {
            color: SliderNavColor.BASE,
            variant: SliderNavVariant.CIRCLE,
        },
        showPagination: false,
        fullWidth: true,
        configurationOverrides: {
            preventClicks: true,
            slidesPerView: SLIDES_AMOUNT_MOBILE,
            spaceBetween: 0,
            watchOverflow: true,
            shouldSwiperUpdate: true,
            breakpoints: {
                [MIN_SCREEN_WIDTH_TABLET]: {
                    slidesPerView: SLIDES_AMOUNT_TABLET,
                },
                [MIN_SCREEN_WIDTH_DESKTOP]: {
                    slidesPerView: SLIDES_AMOUNT_DESKTOP,
                },
                [MIN_SCREEN_WIDTH_DESKTOP_LARGE]: {
                    slidesPerView: SLIDES_AMOUNT_DESKTOP_LARGE,
                },
            },
        },
    };
    const gridAreasStyle = {
        '--pdp-comparison-grid-template-areas': GRID_TEMPLATE_AREAS,
    };
    return (_jsxs(Box, { innerRef: productGroupRef, paddingBlockStart: { base: '4', md: '0' }, paddingBlockEnd: { md: '12', lg: '14' }, "data-analytics-bannerid": analyticsAreaName, testId: testId, children: [title && (_jsx(TextAlpha, { as: "h2", variant: "subheadingL", testId: generateTestId({ testId, suffix: 'title' }), children: title })), _jsx(Box, { paddingBlockStart: { md: '6' }, children: _jsx(Box, { paddingBlockStart: "4", paddingBlockEnd: { base: '6', md: '0' }, marginInline: { base: '-2', md: '-4' }, testId: generateTestId({ testId, suffix: 'carousel-container' }), children: _jsx("div", { style: gridAreasStyle, children: _jsx(Carousel, Object.assign({}, carouselConfig, { children: productsWithFeatures.map((compareProduct, productIndex) => (_jsx("div", { children: _jsx(ProductGroupPdpComparisonProduct, { compareItems: compareItems, hideTags: Boolean(currentProduct && productIndex !== 0), customTags: compareProduct.productId === (currentProduct === null || currentProduct === void 0 ? void 0 : currentProduct.productId) ? createCurrentProductTag() : undefined, onWishListToggle: handleWishlistToggle, parentId: productGroupId, product: compareProduct, productIndex: productIndex, productTileFormat: "simple", updateCompareList: handleUpdateCompareList, onTileClick: () => {
                                        handleItemClickAnalytics(productIndex + COMPARE_PRODUCT_INDEX_ADDITION, compareProduct);
                                    } }) }, `${productUniqueKey}-${compareProduct.productCode}_${compareProduct.colourId}`))) })) }) }) })] }));
};
